/**
 * DetailSurfaceContext — how a tab reaches the panes around it.
 *
 * A detail page's content components already take a lot of props and forward
 * most of them untouched; threading "open the reader with this document",
 * "jump to that tab" and "select this row" through them would make a wide
 * switch wider for the sake of a few calls made deep inside two tabs.
 *
 * So the surface publishes a small API instead, and only the components that
 * actually use it read the context.
 *
 * IT IS OPTIONAL ON PURPOSE. The hook returns null outside its provider, so a
 * tab component rendered anywhere else keeps working: call sites fall back to
 * whatever they did before the surface existed (usually the global side
 * panel).
 *
 * Generic over the page's pane ids, its selection kinds and the shape of
 * whatever its reader pane shows, so each page binds its own typed pair —
 * see `Matters/surface/MatterSurfaceContext`.
 */

import React, { createContext, useContext } from 'react';

/**
 * What the shared DETAIL container is editing.
 *
 * One selection at a time across the whole workspace: picking a task while a
 * question is open swaps the container's content rather than stacking. `id`
 * is the item's STABLE client id — never an array index, which is what made
 * per-tab editors edit the wrong row after a refresh reordered the list.
 */
export interface DetailSelection<Kind extends string = string> {
  kind: Kind;
  /** Stable id of the item, or `null` in create mode. */
  id: string | null;
  /** `create` opens the container empty for a new item of this kind. */
  mode: 'edit' | 'create';
}

/**
 * THE API IS IN THIRDS, and a page only gets the thirds it has.
 *
 * Everything below used to be one interface, so a page with no detail pane
 * still carried `select`, `clearSelection` and a `selection` that was always
 * null — inert, but typed, which meant the project pilot had to pin its
 * selection `Kind` to `string` to name a type it never uses. The halves are
 * separate now and composed per page: panes always, the reader when there is
 * one, the selection when there is a detail pane.
 */

/** What every surface can do: move its panes and its canvas. */
export interface DetailPaneApi<Id extends string = string> {
  /** Open a pane and raise it. */
  openPane: (id: Id) => void;
  closePane: (id: Id) => void;
  isPaneOpen: (id: Id) => boolean;
  /** Switch the canvas to a tab. Unknown tabs are ignored. */
  goToTab: (tab: string) => void;
}

/**
 * A surface with a reader pane.
 *
 * `Reader` defaults to `never`, so a page that declares no reader pane cannot
 * typecheck a call to `openReader` — which is exactly the truth: there is
 * nowhere for the content to go.
 */
export interface DetailReaderApi<Reader = never> {
  /**
   * Show something in the reader pane, opening it if it is closed.
   *
   * Called more than once for the same document — first with `loading: true`
   * while the blob streams, then again with the content.
   */
  openReader: (content: Reader) => void;
}

/** A surface with the shared DETAIL container. */
export interface DetailSelectionApi<Kind extends string = string> {
  /** What the detail pane is showing, or null when it is closed. */
  selection: DetailSelection<Kind> | null;
  /** Open the detail pane on an item. Replaces whatever it was showing. */
  select: (selection: DetailSelection<Kind>) => void;
  /** Close the detail pane and deselect. */
  clearSelection: () => void;
}

/**
 * A surface with no detail pane: panes, tabs and (optionally) a reader.
 *
 * This is what a page bound with `useSurfaceWithoutSelection` publishes.
 */
export type DetailSurfaceApiWithoutSelection<
  Id extends string = string,
  Reader = never,
> = DetailPaneApi<Id> & DetailReaderApi<Reader>;

/** The whole API: panes, reader and the shared detail container. */
export type DetailSurfaceApi<
  Id extends string = string,
  Kind extends string = string,
  Reader = unknown,
> = DetailSurfaceApiWithoutSelection<Id, Reader> & DetailSelectionApi<Kind>;

export interface DetailSurfaceContextBinding<Api> {
  Context: React.Context<Api | null>;
  Provider: React.FC<{ value: Api; children: React.ReactNode }>;
  /** Null outside the surface — callers fall back to their old behaviour. */
  useSurface: () => Api | null;
}

/**
 * Make one page's typed context.
 *
 * A factory rather than a single shared context because two surfaces mounted
 * in the same tree (a matter page that later embeds a review) must not read
 * each other's API, and because the pane ids differ per page.
 *
 * Generic over the whole API TYPE rather than over (Id, Kind, Reader), so a
 * page passes whichever composition it actually has:
 *
 *     createDetailSurfaceContext<MatterSurfaceApi>()   // with a selection
 *     createDetailSurfaceContext<ProjectSurfaceApi>()  // without one
 */
export function createDetailSurfaceContext<
  Api extends DetailPaneApi<any> = DetailSurfaceApi,
>(): DetailSurfaceContextBinding<Api> {
  const Context = createContext<Api | null>(null);

  const Provider: React.FC<{
    value: Api;
    children: React.ReactNode;
  }> = ({ value, children }) => (
    <Context.Provider value={value}>{children}</Context.Provider>
  );

  return {
    Context,
    Provider,
    useSurface: () => useContext(Context),
  };
}

export default createDetailSurfaceContext;
